import { models } from "powerbi-client";
import { generatePowerBIThemes } from "../../assets/powerbi-themes/theme-generator";
import { storage } from "../../utils/helpers/storage";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

// Helper function to generate component keys with conditional logic for kiosk vs normal mode
export const getComponentKey = (baseKey, selectedDashboard, stickyUpdateTrigger) => {
  const isKiosk = storage.getKioskId();
  return isKiosk 
    ? `${baseKey}-${selectedDashboard?.reportId}` // Stable key for kiosk based on reportId
    : `${baseKey}-${selectedDashboard?.name}-${stickyUpdateTrigger}`; // Dynamic key for normal mode
};

// Helper function to generate section filter keys with conditional logic for kiosk vs normal mode
export const getSectionFilterKey = (reportName, section, selectedDashboard, stickyUpdateTrigger) => {
  const isKiosk = storage.getKioskId();
  return isKiosk 
    ? `powerbi-report-section-filters-${reportName}-${section?.displayName}-${selectedDashboard?.reportId}-${section?._id || section?.id || section?.order}`
    : `powerbi-report-section-filters-${reportName}-${section?.displayName}-${section?._id || section?.id || section?.order}-${stickyUpdateTrigger}`;
};

// Cache for generated themes with theme key
const themeCache = new Map();

// Helper to clear theme cache when MUI theme changes
export const clearThemeCache = () => {
  themeCache.clear();
};

export const PowerBIReportState = {
  PENDING: "pending",
  LOADED: "loaded",
  RENDERED: "rendered",
  ERROR: "error",
  PARTIAL_ERROR: "error:partial",
  SERVICE_ERROR: "error:service",
  TOKEN_EXPIRED: "error:tokenexpired",
  NETWORK_ERROR: "error:networkerror",
};

export const getInitialFilter = (databaseName) => {
  return {
    $schema: "http://powerbi.com/product/schema#advanced",
    target: {
      table: "databaseInfo",
      column: "databaseSelected",
    },
    filterType: models.FilterType.Advanced,
    logicalOperator: "Or",
    conditions: [
      {
        operator: "Is",
        value: databaseName,
      },
    ],
  };
};

export const getDisplayOptionFromKioskMode = (kioskMode) => {
  return kioskMode
    ? models.DisplayOption.FitToPage
    : models.DisplayOption.FitToWidth;
};

export const getBookmarkNameFromTheme = (theme, selectedDashboard) => {
  return theme === "dark" && selectedDashboard?.darkBookmark
    ? selectedDashboard.darkBookmark
    : "";
};

export const getThemeFileFromTheme = (theme, muiTheme, isMobile) => {
  if (!muiTheme) {
    return null; // Return null instead of empty object to trigger fallback behavior
  }

  // Create cache key based on theme properties that affect PowerBI rendering
  const themeKey = `${theme}-${isMobile}-${muiTheme.palette.mode}-${muiTheme.palette.primary.main}`;

  // Check cache first
  if (themeCache.has(themeKey)) {
    return themeCache.get(themeKey);
  }

  // Generate themes only when not cached
  const themes = generatePowerBIThemes(muiTheme, isMobile);
  const resultTheme = theme === "dark" ? themes.dark : themes.light;

  if (!resultTheme) {
    return null;
  }

  // Cache the generated theme
  themeCache.set(themeKey, resultTheme);

  // Limit cache size to prevent memory leaks
  if (themeCache.size > 10) {
    const firstKey = themeCache.keys().next().value;
    themeCache.delete(firstKey);
  }

  return resultTheme;
};

export const getDisplayLayoutType = () => {
  return window.innerWidth <= 660 &&
    window.matchMedia("(orientation: portrait)").matches
    ? models.LayoutType.MobilePortrait
    : models.LayoutType.Custom;
};

export const getInitialReportConfig = (
  filters,
  slicers,
  kioskMode,
  theme,
  selectedPage,
  muiTheme,
  isMobile,
) => {
  return {
    type: "report",
    embedUrl: undefined,
    tokenType: models.TokenType.Embed,
    accessToken: undefined,
    id: "",
    permissions: 0,
    datasetBinding: {
      datasetId: "notOriginalDatasetId",
    },
    settings: {
      panes: {
        filters: {
          expanded: false,
          visible: false,
        },
        pageNavigation: {
          visible: false,
        },
      },
      layoutType: getDisplayLayoutType(),
      customLayout: {
        displayOption: getDisplayOptionFromKioskMode(kioskMode),
      },
    },
    bookmark: {},
    theme: {
      themeJson: getThemeFileFromTheme(theme, muiTheme, isMobile),
    },
    slicers: slicers || [],
    filters: [filters],
    pageName: selectedPage,
  };
};

export const getHydratedReportConfig = ({
  data,
  filters,
  slicers,
  reportDisplaySettings,
  selectedPage,
  theme,
  muiTheme,
  isMobile,
}) => {
  if (!data?.token) {
    console.error("PowerBI: Missing or invalid access token.");
    return null; // Prevent invalid config from being used
  }
  return {
    type: "report",
    tokenType: models.TokenType.Embed,
    permissions: 0,
    pageName: selectedPage,
    embedUrl: data.embedUrl,
    accessToken: data.token,
    id: data.reportId,
    datasetBinding: {
      datasetId: data.datasetId,
    },
    slicers: slicers?.length ? slicers : [],
    filters: filters,
    theme: muiTheme
      ? { themeJson: getThemeFileFromTheme(theme, muiTheme, isMobile) }
      : undefined,
    ...reportDisplaySettings,
  };
};

export const getParamFilters = () => {
  const params = new URLSearchParams(window.location.search);

  return Array.from(params.keys()).reduce(
    (acc, val) => ({ ...acc, [val]: params.get(val) }),
    {},
  );
};

export const hydrateFilter = (item) => {
  let filter;
  if (item.filterType === "basic") {
    filter = {
      selector: {
        $schema: "http://powerbi.com/product/schema#slicerTargetSelector",
        target: {
          table: item.table,
          column: item.column,
        },
      },
      state: {
        filters: [
          {
            $schema: "http://powerbi.com/product/schema#basic",
            target: {
              table: item.table,
              column: item.column,
            },
            operator: item.operator,
            values: item.values,
            filterType: models.FilterType.Basic,
          },
        ],
      },
    };
  } else if (item.filterType === "advanced") {
    filter = {
      selector: {
        $schema: "http://powerbi.com/product/schema#advanced",
        target: {
          table: item.table,
          column: item.column,
        },
      },
      state: {
        filters: [
          {
            $schema: "http://powerbi.com/product/schema#advanced",
            target: {
              table: item.table,
              column: item.column,
            },
            // operator: item.operator,
            logicalOperator: item.operator,
            // values: item.values,
            conditions: item.values,
            filterType: models.FilterType.Advanced,
          },
        ],
      },
    };
  }
  return filter;
};

export const addDatasetToFilters = (filters, db) => {
  let _filters = filters;
  _filters.conditions[0].value = db;
  return _filters;
};
export const isRefreshTokenExpiring = (tokenExpiration) => {
  if (!tokenExpiration) return true;
  // Get the current time
  const currentTime = Date.now();
  const expiration = Date.parse(tokenExpiration);

  // Time until token expiration in milliseconds
  const timeUntilExpiration = expiration - currentTime;
  const timeToUpdate = 8 * 60 * 1000; // 8 mins

  // `true` if expiring
  return timeUntilExpiration <= timeToUpdate;
};

/**
 * Checks if the filters include the current day in the selected site's local time zone.
 * @param {Array} filters - PowerBI filters array
 * @param {Object} selectedSite - Site object with address.timezone
 * @returns {boolean}
 */
export const isCurrentDayInFilters = (filters, selectedSite) => {
  if (!filters || !selectedSite?.address?.timezone) {
    return false;
  }

  // Find start and end date filters
  const startDateFilter = filters.find(
    (f) =>
      f.target?.table === "startDate" &&
      f.target?.column === "selectedStartDate" &&
      Array.isArray(f.conditions) &&
      f.conditions[0]?.value,
  );
  const endDateFilter = filters.find(
    (f) =>
      f.target?.table === "endDate" &&
      f.target?.column === "selectedEndDate" &&
      Array.isArray(f.conditions) &&
      f.conditions[0]?.value,
  );

  if (!startDateFilter || !endDateFilter) return false;

  // Parse dates in site timezone
  const tz = selectedSite.address.timezone;
  const startRaw = startDateFilter.conditions[0].value;
  const endRaw = endDateFilter.conditions[0].value;

  // Parse as UTC, then convert to site timezone
  let start = dayjs.utc(startRaw).tz(tz);
  let end = dayjs.utc(endRaw).tz(tz);

  // Current date/time in site timezone
  const now = dayjs().tz(tz);

  // Only return true if current day is within the filter range (inclusive)
  const isInRange = now.isSameOrAfter(start) && now.isSameOrBefore(end);
  return isInRange;
};
